import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ColumnChart, type Series } from '@/components/ColumnChart'
import { describeFailure } from '@/lib/failures'
import { fmtShortDay, fmtTime } from '@/lib/format'
import type { BoardTelemetry } from '@/lib/runs'

const WINDOWS = [['30m', 'Last 30 minutes'], ['1h', 'Last hour'], ['24h', 'Last 24 hours'], ['all', 'Last 30 days']] as const
const SPEND: Series[] = [
  { label: 'Model', color: 'var(--chart-model)' },
  { label: 'EC2', color: 'var(--chart-ec2)' },
]
const FAILURES: Series[] = [{ label: 'Failed attempts', color: 'var(--chart-failure)' }]

function usd(value: number) {
  if (value === 0) return '$0'
  if (value < 0.01) return `$${value.toFixed(4)}`
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
function count(value: number | null) { return value == null ? '—' : value.toLocaleString() }
function duration(seconds: number) {
  if (seconds >= 86400) return `${Math.floor(seconds / 86400)}d`
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h`
  return `${Math.max(1, Math.floor(seconds / 60))}m`
}

/** Historical metrics share a time window; the live fleet sits outside that control. */
export function RunTelemetry({ telemetry, window: selectedWindow, onWindowChange }: {
  telemetry: BoardTelemetry; window?: string; onWindowChange?: (value: string) => void
}) {
  const [localWindow, setLocalWindow] = useState<string>('30m')
  const window = selectedWindow ?? localWindow
  const setWindow = onWindowChange ?? setLocalWindow
  const current = telemetry.windows[window] ?? telemetry.windows['30m']
  const label = WINDOWS.find(([key]) => key === window)?.[1] ?? window
  const free = telemetry.failure_free
  const failure = describeFailure(free?.last_failure?.error_class)
  const statisticsUnavailable = telemetry.statistics?.status === 'error'
  const daily = current.bucket_seconds >= 86400
  const at = (start: string) => daily ? `on ${fmtShortDay(start)}` : `in the ${Math.round(current.bucket_seconds / 60)} min from ${fmtTime(start)}`
  const tick = (start: string) => daily ? fmtShortDay(start) : fmtTime(start)
  const sources = current.cost_sources
  const stale = telemetry.stale
  const priced = !stale && current.model_cost_complete && current.ec2_cost_complete
  const missingUsage = (current.usage_missing_attempts ?? 0) > 0
  const partialUsage = (current.usage_partial_attempts ?? 0) > 0
  const missingPrice = (current.price_missing_attempts ?? 0) > 0
  // Older APIs only expose completeness: never guess which input was missing.
  const usageUnknown = current.usage_missing_attempts == null && !current.model_cost_complete
  const gaps = [
    !sources && missingUsage && `Usage unavailable for ${current.usage_missing_attempts} attempts`,
    !sources && partialUsage && `Partial usage for ${current.usage_partial_attempts} attempts`,
    !sources && missingPrice && 'Model pricing unavailable',
    !current.model_cost_complete && (sources || (!missingUsage && !partialUsage && !missingPrice)) && 'Model spend incomplete or unavailable',
    !current.ec2_cost_complete && ((current.ec2_unpriced_machines ?? 0) > 0 ? `EC2 estimate incomplete · ${current.ec2_unpriced_machines} unpriced machines at peak` : 'EC2 estimate incomplete'),
    stale && 'Spend refresh is overdue',
    sources && sources.model.status !== 'ok' && `OpenRouter: ${sources.model.status}`,
    sources && sources.ec2.status !== 'ok' && `CloudWatch: ${sources.ec2.status}`,
  ].filter(Boolean).join(' · ')
  const subtotal = current.model_cost_usd + current.ec2_cost_usd
  const modelLabel = !current.model_cost_complete && !current.model_cost_usd ? 'Unavailable' : `${usd(current.model_cost_usd)}${current.model_cost_complete ? '' : '+'}`
  const ec2Label = !current.ec2_cost_complete && !current.ec2_cost_usd ? 'Unavailable' : `${usd(current.ec2_cost_usd)}${current.ec2_cost_complete ? '' : '+'}`
  const tokenLabel = missingUsage || partialUsage || usageUnknown
    ? current.tokens ? `${count(current.tokens)} recorded tokens` : 'Token usage unavailable'
    : `${count(current.tokens)} tokens`

  return <section className="run-overview" aria-label="Run overview">
    <div className="run-overview-toolbar">
      <div className="segmented" role="group" aria-label="Time window">
        {WINDOWS.map(([key, name]) => <button type="button" key={key} aria-pressed={window === key}
          className={window === key ? 'selected' : ''} onClick={() => setWindow(key)} title={name}>{key === 'all' ? '30d' : key}</button>)}
      </div>
    </div>
    <div className="run-overview-metrics">
      <article className="run-metric">
        <h2>Spend <span>{label}</span></h2>
        <strong className="run-metric-value">{!priced && !subtotal ? '—' : usd(subtotal)}{!priced && subtotal > 0 && <small title="Known subtotal; some costs are unavailable"> +</small>}</strong>
        <p className="run-cost-key"><span><i style={{ background: 'var(--chart-model)' }} />{modelLabel} model</span><span><i style={{ background: 'var(--chart-ec2)' }} />{ec2Label} EC2 estimate</span></p>
        {!priced && <p className="muted small" title="OpenRouter history before the dedicated key was activated is unattributed. Missing metric samples and unpriced machines make EC2 estimates incomplete.">{gaps}</p>}
        <div className="metric-trend" aria-label="Spend over time">
          <ColumnChart key={window} compact series={SPEND} format={usd} caption={at} tick={tick} emptyLabel={priced ? 'No recorded spend in this period.' : 'Cost data unavailable for this period.'}
            columns={current.series.map(item => ({ start: item.start, parts: [item.model_cost_usd, item.ec2_cost_usd] }))} />
        </div>
        {sources ? <p className="metric-context">OpenRouter recorded spend · EC2 estimate · {sources.environment}</p>
          : <p className="metric-context">{tokenLabel} · {priced ? 'Estimated cost' : 'Partial cost'}</p>}
      </article>
      <article className="run-metric">
        <h2>Failed attempts <span>{label}</span></h2>
        {statisticsUnavailable ? <>
          <strong className="run-metric-value">—</strong>
          <p role="status">Attempt statistics unavailable</p>
          <p className="metric-context">{telemetry.statistics?.updated_at
            ? `Last successful refresh ${fmtTime(telemetry.statistics.updated_at)}` : 'No successful refresh yet'}</p>
        </> : <>
        <strong className="run-metric-value">{count(current.failed_attempts)}<small> / {count(current.attempts)}</small></strong>
        <p>{current.failed_attempts ? `${count(current.failed_jobs)} affected ${current.failed_jobs === 1 ? 'job' : 'jobs'} · includes retries` : 'All attempts clear'}</p>
        <div className="metric-trend" aria-label="Failures over time">
          <ColumnChart key={window} compact series={FAILURES} format={count} caption={at} tick={tick} emptyLabel="No failures in this period."
            columns={current.series.map(item => ({ start: item.start, parts: item.failed_attempts == null ? [] : [item.failed_attempts] }))} />
        </div>
        <p className="metric-context">{free?.last_failure
          ? <Link title={`${failure?.label ?? 'Failure'} in ${free.last_failure.run_name}`} to={`/eng/runs/${free.last_failure.run_id}?job=${free.last_failure.job_id}`}>Last failure · {duration(free.seconds)} ago</Link>
          : `No failures since ${fmtShortDay(telemetry.since)}`}</p>
        </>}
      </article>
    </div>
    <div className="run-spend-sources muted small">
      {sources && <>
        <p>Model data {sources.model.updated_at ? `updated ${fmtTime(sources.model.updated_at)}` : 'unavailable'} · EC2 data {sources.ec2.updated_at ? `updated ${fmtTime(sources.ec2.updated_at)}` : 'unavailable'}</p>
        {(current.model_byok_usd ?? 0) > 0 && <p>Includes {usd(current.model_credits_usd ?? 0)} OpenRouter credits and {usd(current.model_byok_usd ?? 0)} BYOK inference.</p>}
      </>}
      {!statisticsUnavailable && <p>Attempt statistics cover the latest {telemetry.runs_scanned} recorded runs.</p>}
    </div>
  </section>
}
